import { useCallback, useEffect, useRef, useState } from "react";
import { DatabaseBackup, PlayCircle, Loader2, RefreshCw, AlertTriangle, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { apiErrorText, errorText } from "@/lib/errorText";
import { useI18n } from "@/context/I18nContext";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Table,
    TableHeader,
    TableBody,
    TableRow,
    TableHead,
    TableCell,
} from "@/components/ui/table";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { OPS } from "@/constants/testIds";
import { toast } from "sonner";

const B = OPS.backups;
const POLL_MS = 15000;
// A `running` row older than this is a dead run (backup_service.STALE_AFTER):
// it must not keep the button disabled or the page polling forever.
const STALE_MS = 6 * 60 * 60 * 1000;

const isLive = (r) =>
    r.status === "running" && !!r.started_at && Date.now() - new Date(r.started_at).getTime() < STALE_MS;

// -------- helpers --------------------------------------------------------
const fmtBytes = (n) => {
    const v = Number(n || 0);
    if (v < 1024) return `${v} B`;
    if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`;
    if (v < 1024 * 1024 * 1024) return `${(v / (1024 * 1024)).toFixed(2)} MB`;
    return `${(v / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const fmtWhen = (iso, lang) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString(lang === "ar" ? "ar-SA" : "en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch (_e) {
        return iso;
    }
};

function StatusBadge({ status, t }) {
    if (status === "ok") {
        return (
            <Badge variant="success" className="gap-1">
                <CheckCircle2 className="h-3 w-3" />
                {t("backups_status_ok")}
            </Badge>
        );
    }
    if (status === "partial") {
        return <Badge variant="warning">{t("backups_status_partial")}</Badge>;
    }
    if (status === "failed") {
        return (
            <Badge variant="destructive" className="gap-1">
                <AlertTriangle className="h-3 w-3" />
                {t("backups_status_failed")}
            </Badge>
        );
    }
    if (status === "running") {
        return (
            <Badge variant="secondary" className="gap-1">
                <Loader2 className="h-3 w-3 animate-spin" />
                {t("backups_status_running")}
            </Badge>
        );
    }
    return <Badge variant="secondary">{status || "—"}</Badge>;
}

// -------- page ----------------------------------------------------------
export default function BackupsPage() {
    const { t, lang } = useI18n();
    const [runs, setRuns] = useState(null);
    const [backupRoot, setBackupRoot] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    // The run this page started: its real result is toasted when the row leaves `running`.
    const watching = useRef(null);

    const load = useCallback(async () => {
        try {
            const r = await api.get("/admin/backups");
            const list = r.data.runs || [];
            setRuns(list);
            setBackupRoot(r.data.backup_root || "");
            setError(null);
            const row = watching.current ? list.find((x) => x.folder === watching.current) : null;
            if (row && row.status !== "running") {
                watching.current = null;
                if (row.status === "failed") toast.error(`${t("backups_run_failed")}: ${errorText(row.error, "")}`);
                else toast.success(t("backups_run_success").replace("{n}", row.totals?.ok ?? 0));
            }
        } catch (e) {
            setError(apiErrorText(e, t("error_generic")));
        }
    }, [t]);

    useEffect(() => {
        load();
    }, [load]);

    // A run takes about 70 minutes: refresh while the server shows one running.
    const activeRun = (runs || []).find(isLive) || null;
    const activeFolder = activeRun?.folder || null;
    useEffect(() => {
        if (!activeFolder) return undefined;
        const id = setInterval(load, POLL_MS);
        return () => clearInterval(id);
    }, [activeFolder, load]);

    const runNow = async () => {
        setConfirmOpen(false);
        if (busy || activeRun) return;
        setBusy(true);
        setError(null);
        try {
            // Returns at once with the `running` row; the dump goes on in the background.
            const r = await api.post("/admin/backups/run");
            watching.current = r.data?.folder || null;
            toast.success(t("backups_run_started"));
            await load();
        } catch (e) {
            // 409 = a run is already going: show it (the list) and say so (the banner).
            const msg = apiErrorText(e, t("error_generic"));
            toast.error(msg);
            await load();
            setError(msg);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid={B.page} className="space-y-5">
            <PageHeader
                icon={DatabaseBackup}
                title={t("backups_title")}
                subtitle={t("backups_subtitle")}
                primaryAction={
                    <Button
                        data-testid={B.runNow}
                        onClick={() => setConfirmOpen(true)}
                        disabled={busy || !!activeRun}
                        className="gap-2"
                    >
                        {busy || activeRun ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />}
                        {busy || activeRun ? t("backups_running") : t("backups_run_now")}
                    </Button>
                }
                actions={
                    <Button variant="soft" size="sm" onClick={load} disabled={busy} className="gap-2">
                        <RefreshCw className="h-3.5 w-3.5" />
                        {t("period_apply")}
                    </Button>
                }
            />

            {error ? (
                <div
                    data-testid={B.errorBanner}
                    className="rounded-xl border border-[color:var(--danger-soft)] bg-[color:var(--danger-soft-bg)] px-4 py-3 text-sm text-[color:var(--danger)]"
                >
                    <div className="flex items-start gap-2">
                        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                        <div className="min-w-0 flex-1 break-words">{error}</div>
                    </div>
                </div>
            ) : null}

            <Card data-testid={B.card} className="rounded-2xl border-[color:var(--border-soft)] shadow-[var(--shadow-sm)]">
                <CardHeader className="pb-3">
                    <CardTitle className="text-base font-bold text-[color:var(--ink)]">
                        {t("backups_last_runs")}
                    </CardTitle>
                    {backupRoot ? (
                        <p data-testid={B.summary} className="text-xs text-[color:var(--ink-muted)]">
                            {t("backups_root_note")} <code className="font-mono">{backupRoot}/</code>
                        </p>
                    ) : null}
                    {activeRun ? (
                        <p data-testid={B.runningBadge} className="flex items-center gap-1.5 text-xs text-[color:var(--ink-muted)]">
                            <Loader2 className="h-3 w-3 animate-spin shrink-0" />
                            {t("backups_active_note")} <bdi dir="ltr">{fmtWhen(activeRun.started_at, lang)}</bdi>
                        </p>
                    ) : null}
                </CardHeader>
                <CardContent>
                    {runs === null ? (
                        <Skeleton className="h-40 w-full" />
                    ) : runs.length === 0 ? (
                        <div
                            data-testid={B.empty}
                            className="rounded-lg border border-dashed border-[color:var(--border-soft)] py-10 text-center text-sm text-[color:var(--ink-muted)]"
                        >
                            {t("backups_empty")}
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table data-testid={B.table}>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>{t("backups_col_folder")}</TableHead>
                                        <TableHead>{t("backups_col_status")}</TableHead>
                                        <TableHead>{t("backups_col_trigger")}</TableHead>
                                        <TableHead>{t("backups_col_started")}</TableHead>
                                        <TableHead className="text-end">{t("backups_col_ok")}</TableHead>
                                        <TableHead className="text-end">{t("backups_col_failed")}</TableHead>
                                        <TableHead className="text-end">{t("backups_col_docs")}</TableHead>
                                        <TableHead className="text-end">{t("backups_col_size")}</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {runs.map((r) => (
                                        <TableRow key={r.folder} data-testid={B.row(r.folder)}>
                                            <TableCell className="font-mono text-xs">{r.folder}</TableCell>
                                            <TableCell data-testid={B.rowStatus(r.folder)}>
                                                <StatusBadge status={r.status} t={t} />
                                            </TableCell>
                                            <TableCell className="text-xs text-[color:var(--ink-muted)]">
                                                {r.triggered_by === "manual"
                                                    ? t("backups_trigger_manual")
                                                    : t("backups_trigger_scheduler")}
                                            </TableCell>
                                            <TableCell className="text-xs">
                                                <bdi dir="ltr">{fmtWhen(r.started_at, lang)}</bdi>
                                            </TableCell>
                                            <TableCell className="text-end font-semibold text-[color:var(--success)]">
                                                {r.totals?.ok ?? 0}
                                            </TableCell>
                                            <TableCell className="text-end font-semibold text-[color:var(--danger)]">
                                                {r.totals?.failed ?? 0}
                                            </TableCell>
                                            <TableCell className="text-end">{r.totals?.doc_count ?? 0}</TableCell>
                                            <TableCell className="text-end">{fmtBytes(r.totals?.size_bytes)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>

            <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                <AlertDialogContent data-testid={B.confirm}>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("backups_confirm_title")}</AlertDialogTitle>
                        <AlertDialogDescription>{t("backups_confirm_body")}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="gap-2">
                        <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
                        <AlertDialogAction data-testid={B.confirmStart} onClick={runNow}>
                            {t("backups_confirm_start")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
